import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { createSignal, type Accessor } from 'solid-js';

// PascalCase to match SignalR MessagePack ContractlessStandardResolver output from C# POCOs.

// Blocks is a raw-DEFLATE blob; the mesh worker inflates it to the 131072-byte raw column.
// Biomes is 256 raw bytes, one biome id per X,Z column (index z*16+x).
export interface ChunkPayload {
  Cx: number;
  Cz: number;
  Blocks: Uint8Array;
  Biomes: Uint8Array;
}

export interface ChunkUnloadPayload {
  Cx: number;
  Cz: number;
}

export interface BlockUpdate {
  Wx: number;
  Wy: number;
  Wz: number;
  Packed: number;
}

export interface BlockBatchPayload {
  Updates: BlockUpdate[];
}

export interface PositionPayload {
  X: number;
  Y: number;
  Z: number;
  Yaw: number;
  Pitch: number;
}

export function createLiveViewClient(botId: string) {
  const conn: HubConnection = new HubConnectionBuilder()
    .withUrl('/hubs/liveview')
    .withHubProtocol(new MessagePackHubProtocol())
    .withAutomaticReconnect()
    .build();

  const [position, setPosition] = createSignal<PositionPayload | null>(null);
  const chunkListeners: ((c: ChunkPayload) => void)[] = [];
  const chunkUnloadListeners: ((u: ChunkUnloadPayload) => void)[] = [];
  const blockBatchListeners: ((b: BlockBatchPayload) => void)[] = [];

  conn.on('Chunk', (c: ChunkPayload) => {
    for (const l of chunkListeners) l(c);
  });
  conn.on('ChunkUnload', (u: ChunkUnloadPayload) => {
    for (const l of chunkUnloadListeners) l(u);
  });
  conn.on('BlockBatch', (b: BlockBatchPayload) => {
    for (const l of blockBatchListeners) l(b);
  });
  conn.on('Position', (p: PositionPayload) => setPosition(p));

  async function connect(): Promise<void> {
    if (conn.state === HubConnectionState.Disconnected) {
      await conn.start();
    }
    await conn.invoke('Subscribe', botId);
  }

  async function dispose(): Promise<void> {
    try {
      if (conn.state === HubConnectionState.Connected) {
        await conn.invoke('Unsubscribe');
      }
    } catch {
      /* already gone */
    }
    try {
      await conn.stop();
    } catch {
      /* already gone */
    }
  }

  function onChunk(fn: (c: ChunkPayload) => void): () => void {
    chunkListeners.push(fn);
    return () => {
      const i = chunkListeners.indexOf(fn);
      if (i >= 0) chunkListeners.splice(i, 1);
    };
  }

  function onChunkUnload(fn: (u: ChunkUnloadPayload) => void): () => void {
    chunkUnloadListeners.push(fn);
    return () => {
      const i = chunkUnloadListeners.indexOf(fn);
      if (i >= 0) chunkUnloadListeners.splice(i, 1);
    };
  }

  function onBlockBatch(fn: (b: BlockBatchPayload) => void): () => void {
    blockBatchListeners.push(fn);
    return () => {
      const i = blockBatchListeners.indexOf(fn);
      if (i >= 0) blockBatchListeners.splice(i, 1);
    };
  }

  // conn.send is fire-and-forget: no round-trip latency for keypress/mouse.
  async function setMovementInput(input: {
    forward: boolean;
    backward: boolean;
    left: boolean;
    right: boolean;
    jump: boolean;
    sneak: boolean;
  }): Promise<void> {
    if (conn.state !== HubConnectionState.Connected) return;
    await conn.send(
      'SetMovementInput',
      input.forward,
      input.backward,
      input.left,
      input.right,
      input.jump,
      input.sneak,
    );
  }

  async function setLookDirection(yaw: number, pitch: number): Promise<void> {
    if (conn.state !== HubConnectionState.Connected) return;
    await conn.send('SetLookDirection', yaw, pitch);
  }

  return {
    connect,
    dispose,
    onChunk,
    onChunkUnload,
    onBlockBatch,
    setMovementInput,
    setLookDirection,
    position: position as Accessor<PositionPayload | null>,
  };
}

export type LiveViewClient = ReturnType<typeof createLiveViewClient>;
